import { useEffect, useState } from 'react'
import { SegmentedControl } from '@mantine/core'
import { type UseFormRegisterReturn, useFormContext } from 'react-hook-form'

export interface SchedulingOption {
  value: string
  label: string
}

interface SchedulingToggleProps {
  label?: string
  required?: boolean
  error?: string
  className?: string
  disabled?: boolean
  options: SchedulingOption[]
  register?: UseFormRegisterReturn
  value?: string
  onChange?: (value: string) => void
  formValue?: string
}

// Custom hook to safely get form context
function useSafeFormContext() {
  try {
    return useFormContext()
  } catch {
    return null
  }
}

export function SchedulingToggle({
  label,
  required = false,
  error,
  className = '',
  disabled = false,
  options,
  register,
  value,
  onChange,
  formValue,
}: SchedulingToggleProps) {
  // Always call the hook - it's now in a separate function
  const formContext = useSafeFormContext()

  const watchedValue =
    formContext && register ? formContext.watch(register.name) : undefined

  // Track the current value with state to ensure updates trigger re-renders
  const [currentValue, setCurrentValue] = useState(() => {
    return register
      ? watchedValue || options[0]?.value
      : (value ?? formValue ?? options[0]?.value)
  })

  // Update currentValue when form value changes
  useEffect(() => {
    if (register && watchedValue !== undefined) {
      setCurrentValue(watchedValue)
    } else {
      setCurrentValue(value ?? formValue ?? options[0]?.value)
    }
  }, [watchedValue, value, formValue, options, register])

  const handleChange = (optionValue: string) => {
    // Update local state immediately for better UX
    setCurrentValue(optionValue)

    if (register) {
      register.onChange({ target: { value: optionValue } })
    } else if (onChange) {
      onChange(optionValue)
    }
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      <SegmentedControl
        value={currentValue}
        onChange={handleChange}
        disabled={disabled}
        data={options.map(option => ({
          value: option.value,
          label: option.label,
        }))}
        radius="md"
        size="md"
        className="w-full"
        color="blue"
        styles={{
          root: {
            backgroundColor: '#F5F5F7',
            padding: '6px',
          },
          indicator: {
            backgroundColor: '#254769',
          },
          control: {
            color: '#878B8F',
            '&[data-active]': {
              color: 'white',
            },
          },
        }}
      />

      {error && <p className="text-sm text-red-600 mt-1">{error}</p>}
    </div>
  )
}
